import { bip39Word } from "../bip39";
import { asBip39WordIndices, BLEND_SEED_LENGTH } from "../scoring";
import type { DailyPuzzleResult, DailyPuzzleSource } from "./daily-puzzle-source";
import type { Blend, DailyPuzzle } from "./types";

/**
 * Resolves the daily puzzle by calling `CogQuest.Service`'s
 * `GET /api/v1/puzzle/today`. Replaces the old in-browser
 * SHA-256-of-the-date derivation: the server now picks the puzzle from
 * an on-chain mining block, so it's authoritative.
 *
 * Wire contract — `PuzzleResponse`, camelCase JSON:
 *
 *   { id, resolvedFor, miningInputBlockHash, domainId,
 *     requiredWords[5], bip39WordIndices[5], blendSeed (64 hex chars) }
 *
 * Status codes the SPA must accept:
 *
 *   200 fresh           — `resolvedFor === id`.
 *   200 b2 fallback     — `resolvedFor` is yesterday; `id` is today.
 *   200 stale-if-error  — `Cache-Control` header carries `stale-if-error`.
 *   404                 — "puzzle pending"; neither today nor yesterday
 *                         is anchored on-chain yet.
 *   503 + Retry-After   — Cx.Api is dead with an empty cache. Caller may
 *                         retry after the suggested delay.
 *
 * Any other non-2xx is thrown — those are bugs on the server side, not
 * states the SPA needs to render.
 */

const DEFAULT_BASE_URL = import.meta.env.VITE_COGQUEST_BASE_URL ?? "";

/// `DailyPuzzleSource` adapter that calls `CogQuest.Service`. Thin
/// class around {@link fetchTodaysPuzzle} so the function stays directly
/// testable with an injected `fetch`, while consumers code against the
/// {@link DailyPuzzleSource} interface.
export class ApiDailyPuzzleSource implements DailyPuzzleSource {
  constructor(
    private readonly baseUrl: string = DEFAULT_BASE_URL,
    private readonly fetchImpl: typeof globalThis.fetch = globalThis.fetch,
  ) {}

  fetchTodaysPuzzle(): Promise<DailyPuzzleResult> {
    return fetchTodaysPuzzle(this.fetchImpl, this.baseUrl);
  }
}

interface PuzzleResponse {
  readonly id: string;
  readonly resolvedFor: string;
  readonly miningInputBlockHash: string;
  readonly domainId: number;
  readonly requiredWords: readonly string[];
  readonly bip39WordIndices: readonly number[];
  readonly blendSeed: string;
}

export async function fetchTodaysPuzzle(
  fetchImpl: typeof globalThis.fetch = globalThis.fetch,
  baseUrl: string = DEFAULT_BASE_URL,
): Promise<DailyPuzzleResult> {
  const response = await fetchImpl(`${baseUrl}/api/v1/puzzle/today`);

  if (response.status === 404) return { status: "pending" };

  if (response.status === 503) {
    return {
      status: "unavailable",
      retryAfterSeconds: parseRetryAfter(response.headers.get("Retry-After")),
    };
  }

  if (!response.ok) {
    throw new Error(`unexpected status ${response.status} from /api/v1/puzzle/today`);
  }

  const body = (await response.json()) as PuzzleResponse;
  const stale = hasStaleIfError(response.headers.get("Cache-Control"));
  const blend = body.requiredWords.slice(0, 5) as unknown as Blend;
  const puzzle: DailyPuzzle = {
    id: body.id,
    resolvedFor: body.resolvedFor,
    miningInputBlockHash: body.miningInputBlockHash,
    domainId: body.domainId,
    blend,
    blendIndices: asBip39WordIndices(body.bip39WordIndices),
    blendSeed: hexToBytes(body.blendSeed, BLEND_SEED_LENGTH),
  };
  // The server is supposed to return the BIP-39 words from
  // `bip39WordIndices`; if it doesn't, prefer the indices we'd otherwise
  // trust the scorer with. (Cheap sanity check, not a strict validator.)
  if (blend.some((w, i) => w !== bip39Word(puzzle.blendIndices[i]!))) {
    throw new Error("requiredWords disagree with bip39WordIndices");
  }
  return { status: "ok", puzzle, stale };
}

/// Parse an HTTP `Retry-After` value. Numeric form is seconds; HTTP-date
/// form returns the seconds until that timestamp. Returns `undefined`
/// when missing or unparseable so the caller can fall back to its own
/// retry cadence.
function parseRetryAfter(header: string | null): number | undefined {
  if (header === null) return undefined;
  const trimmed = header.trim();
  if (trimmed === "") return undefined;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const date = Date.parse(trimmed);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

function hasStaleIfError(header: string | null): boolean {
  if (header === null) return false;
  return /(^|[\s,])stale-if-error(\s*=|\s*,|$)/i.test(header);
}

function hexToBytes(hex: string, expectedLength: number): Uint8Array {
  if (hex.length !== expectedLength * 2) {
    throw new Error(`expected ${expectedLength * 2} hex chars; got ${hex.length}`);
  }
  const out = new Uint8Array(expectedLength);
  for (let i = 0; i < expectedLength; i++) {
    const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new Error(`invalid hex byte at offset ${i * 2}`);
    out[i] = byte;
  }
  return out;
}
